import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Toast from '../components/Toast';
import { useRescoreJobPoller } from '../hooks/useRescoreJobPoller.js';

const ToastContext = createContext(null);

/**
 * ToastProvider - Lightweight shared toast notification system
 *
 * Replaces silent console.error patterns with user-visible feedback.
 * Only one toast of each type is shown at a time (replaces, not queues).
 *
 * @see GitHub Issue #515
 */
export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const showToast = useCallback((message, type = 'error', duration = 5000, action = null) => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setToast({ message, type, action });

    timerRef.current = setTimeout(() => {
      setToast(null);
      timerRef.current = null;
    }, duration);
  }, []);

  const dismissToast = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setToast(null);
  }, []);

  // Issue #1681: app-lifetime check for background rescore-job completion —
  // see useRescoreJobPoller.js. Mounted here (not a page component) so the
  // completion toast survives both a navigate-away from ScenarioAuthoringPage
  // and a `returnTo` redirect that bypasses ScenariosPageWorking.
  useRescoreJobPoller(showToast);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          action={toast.action}
          onDismiss={dismissToast}
        />
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Same contract as useToast but no-ops when no provider is mounted (tests,
 * SSR static-markup paths). Use in components that may render outside the
 * full app shell (e.g., Issue #1050: NotableActivityFeed renders both inside
 * the dashboard ToastProvider AND in renderToStaticMarkup tests).
 */
export function useToastSafe() {
  const context = useContext(ToastContext);
  if (!context) {
    return { showToast: () => {} };
  }
  return context;
}
